import React, { useContext, useEffect } from 'react';
import { useGetMetric } from '../../../../GoalShow/useGoalData';
import useMetricUsageReport from '../../../../../hooks/useMetricUsageReport';
import { FindAndReplace } from '../types';
import { FindAndReplaceContext } from './FindAndReplace';
import CurrentUserContext from '../../../../../contexts/CurrentUserContext';
import UsageTab from '../../MetricSlideOut/UsageTab';
import Typography from '../../../../../kingpin/atoms/Typography';
import { Scrollable } from '../../../../../components/UserPicker/UserPicker';
import Alert from '../../../../../kingpin/Alert';
import getUsageCount from './useFindAndReplace/getUsageCount';
import isCompoundMetric from '../../../../../types/metricTypeCheckers/isCompoundMetric';
import isNormalMetric from '../../../../../types/metricTypeCheckers/isNormalMetric';
import getIdentifier from '../../../../../getIdentifier';
import getTimeStamp from '../../../../../getTimeStamp';

const FindAndReplaceUsage = () => {
  const getMetric = useGetMetric();
  const [initialUsageCount, setInitialUsageCount] = React.useState<number>(0);
  const [usageCount, setUsageCount] = React.useState<number>(0);
  const {
    replaceMetricId,
    findMetricId,
    setInitialAffectedContent,
    setSelectedAffectedContent,
    selectedAffectedContent,
  } = useContext(FindAndReplaceContext);
  const { id: currentUserId } = useContext(CurrentUserContext);

  const findMetric = getMetric(findMetricId);

  if (!isCompoundMetric(findMetric) && !isNormalMetric(findMetric)) {
    const e = new Error('');
    e.name = 'FindAndReplace: Only normal and compound metrics are supported';
    throw e;
  }

  const affectedContent = useMetricUsageReport(findMetric);

  useEffect(() => {
    if (selectedAffectedContent?.affectedContentIds) {
      const usageCount = getUsageCount(
        selectedAffectedContent?.affectedContentIds,
      );
      setUsageCount(usageCount);
    }
  }, [selectedAffectedContent]);

  useEffect(() => {
    if (affectedContent) {
      const metricReceipt: FindAndReplace.MetricReceipt = {
        replaceMetricId,
        findMetricId,
        createdOn: getTimeStamp(),
        createdBy: currentUserId,
        id: getIdentifier(),
        affectedContentIds: {
          boards: affectedContent.boards.map((b) => b.id),
          goals: affectedContent.goals.map((g) => g.id),
          scorecards: affectedContent.scorecards.map((s) => s.id),
          dashboardGadgets: affectedContent.dashboardGadgets.map(
            (d) => d.gadgetId,
          ),
          chartDefinitions: affectedContent.chartDefinitions.map((c) => c.id),
          compoundMetrics: affectedContent.compoundMetrics.map((cm) => cm.id),
        },
      };

      setInitialAffectedContent(metricReceipt);
      setSelectedAffectedContent(metricReceipt);
      setInitialUsageCount(getUsageCount(metricReceipt.affectedContentIds));
    }
  }, [
    affectedContent,
    findMetric,
    setInitialAffectedContent,
    setSelectedAffectedContent,
    currentUserId,
    findMetricId,
    replaceMetricId,
  ]);

  return (
    <div>
      <Alert
        type={'Info'}
        title={'Select items to replace'}
        body={
          'Select the items using the current metric which you would like to replace with the new metric (Compound Metrics and Datasets are currently not supported):'
        }
      ></Alert>
      <Scrollable
        style={{
          maxHeight: 400,
          border: '1px solid var(--color-border-tertiary)',
          marginTop: 5,
          padding: 4,
        }}
      >
        <UsageTab hideReports metric={findMetric}></UsageTab>
      </Scrollable>
      <Typography.Body type={'Body 13'}>
        Replacing <b>{usageCount}</b> of <b>{initialUsageCount}</b> items
      </Typography.Body>
    </div>
  );
};

export default FindAndReplaceUsage;
